import React from "react";
import { Row, Col } from "react-bootstrap";
import { RBSModal } from "../../base/rbs_components/RBSModal.tsx";
import { RBSTextField } from "../../base/rbs_components/RBSTextField.tsx";
import { RBSDropdown } from "../../base/rbs_components/RBSDropdown.tsx";
import type {
    CashReserveInput,
    CashReserve,
    WaterfallItemInput,
    WaterfallItem,
    TimeSeries,
    Variable
} from "../../base/ui_general/GeneralInterfaces.tsx";
import { WaterfallItemTypeEnum, LabelTextMessageTypes, ValidationSchemes, TextfieldInputTypes } from "../../base/Settings.tsx";
import { getLabelTextMessage } from "../../base/utils/GeneralUtilities.tsx";
import type { InputStateControl } from "../../base/utils/InputStateControl.tsx";
import type { UserLanguages } from "../../base/Settings.tsx";

/**
 * Props for CashReserveDetailModal.
 * This interface describes the data structure for displaying and editing a Cash Reserve in a modal dialog.
 */
interface CashReserveDetailModalProps {
    /** Indicates whether the modal is visible. */
    show: boolean;
    /** The CashReserve to display or edit (main type, not input). */
    cashReserve?: CashReserve;
    /** The associated WaterfallItem to display or edit. */
    waterfall_item?: WaterfallItem;
    /** Callback to submit the cash reserve input. */
    onSubmit: (cashReserveInput: CashReserveInput, waterfallItemInput: WaterfallItemInput) => void;
    /** Callback to close the modal. */
    onClose: () => void;
    /** Indicates if the form input is valid. */
    isValid: boolean;
    /** InputStateControl instance for managing input state and validation. */
    inputStateControl: InputStateControl;
    /** Indicates the preferred user language. */
    user_language: UserLanguages;
    /** Array of available time series for base_curve_id selection. */
    time_series: Array<TimeSeries> | undefined;
    /** Array of available variables for reference_variable_id selection. */
    variables: Array<Variable> | undefined;
}

export const CashReserveDetailModal: React.FC<CashReserveDetailModalProps> = (props: CashReserveDetailModalProps) => {
    // Helper to prefill values from cashReserve or waterfall_item, or use defaults
    const prefill = (key: string): string => {
        if (props.cashReserve && key in props.cashReserve) {
            const value = props.cashReserve[key as keyof typeof props.cashReserve];
            // Example: if you add invested dropdown, convert to string
            return String(value ?? "");
        }
        if (props.waterfall_item && key in props.waterfall_item) {
            const value = props.waterfall_item[key as keyof typeof props.waterfall_item];
            return String(value ?? "");
        }
        switch (key) {
            case "base_curve_id":
            case "reference_variable_id":
                return "";
            case "name":
                return "New Cash Reserve";
            case "description":
                return "Cash reserve for transaction";
            case "initial_nominal":
                return "1000000";
            case "reserve_rate":
                return "0.05";
            case "spread_receiving":
                return "0.01";
            case "spread_paying":
                return "0.005";
            default:
                return "";
        }
    };
    const headerTitle = props.cashReserve
        ? getLabelTextMessage(LabelTextMessageTypes.UPDATE_CASH_RESERVE_TITLE, props.user_language)
        : getLabelTextMessage(LabelTextMessageTypes.NEW_CASH_RESERVE_CREATION_TITLE, props.user_language);
    return (
        <RBSModal
            show={props.show}
            header_title={headerTitle}
            show_close_button
            onClose={props.onClose}
            primary_button_label={getLabelTextMessage(LabelTextMessageTypes.SUBMIT, props.user_language)}
            secondary_button_label={getLabelTextMessage(LabelTextMessageTypes.CANCEL, props.user_language)}
            onClickSecondary={props.onClose}
            primary_button_disabled={!props.isValid}
            size={"xl"}
            onClickPrimary={() => {
                const baseCurveIdRaw = props.inputStateControl.getStringValueForKey("base_curve_id");
                let transactionIdRaw = props.inputStateControl.getStringValueForKey("transaction_id");
                if (!transactionIdRaw) transactionIdRaw = "";
                const cashReserveInput: CashReserveInput = {
                    initial_nominal: Number(props.inputStateControl.getStringValueForKey("initial_nominal")),
                    reserve_rate: Number(props.inputStateControl.getStringValueForKey("reserve_rate")),
                    spread_receiving: Number(props.inputStateControl.getStringValueForKey("spread_receiving")),
                    spread_paying: Number(props.inputStateControl.getStringValueForKey("spread_paying")),
                    base_curve_id: baseCurveIdRaw === "" ? undefined : baseCurveIdRaw,
                    reference_variable_id: props.inputStateControl.getStringValueForKey("reference_variable_id") ?? ""
                };
                const waterfallItemInput: WaterfallItemInput = {
                    name: props.inputStateControl.getStringValueForKey("name") ?? "",
                    description: props.inputStateControl.getStringValueForKey("description") ?? "",
                    transaction_id: transactionIdRaw,
                    type: WaterfallItemTypeEnum.CASH_RESERVE,
                };
                props.onSubmit(cashReserveInput, waterfallItemInput);
                props.onClose();
            }}
        >
            <Row>
                <Col md={6}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.NAME, props.user_language)}
                        input_key="name"
                        prefill={prefill("name")}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.TEXT}
                    />
                </Col>
                <Col md={6}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.INITIAL_NOMINAL, props.user_language)}
                        input_key="initial_nominal"
                        prefill={prefill("initial_nominal")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.RESERVE_RATE, props.user_language)}
                        input_key="reserve_rate"
                        prefill={prefill("reserve_rate")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
                <Col md={4}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.SPREAD_RECEIVING, props.user_language)}
                        input_key="spread_receiving"
                        prefill={prefill("spread_receiving")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
                <Col md={4}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.SPREAD_PAYING, props.user_language)}
                        input_key="spread_paying"
                        prefill={prefill("spread_paying")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <RBSDropdown
                        label={getLabelTextMessage(LabelTextMessageTypes.BASE_CURVE_ID, props.user_language)}
                        input_key="base_curve_id"
                        prefill={prefill("base_curve_id")}
                        items={[{ label: "---", value: "" }, ...(props.time_series ? props.time_series.map(ts => ({ label: ts.name, value: ts.id })) : [])]}
                        input_state_control={props.inputStateControl}
                        validation_schemes={[]}
                    />
                </Col>
                <Col md={4}>
                    <RBSDropdown
                        label={getLabelTextMessage(LabelTextMessageTypes.REFERENCE_VARIABLE_ID, props.user_language)}
                        input_key="reference_variable_id"
                        prefill={prefill("reference_variable_id")}
                        items={[{ label: "---", value: "" }, ...(props.variables ? props.variables.map(v => ({ label: v.name, value: v.id })) : [])]}
                        input_state_control={props.inputStateControl}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.DESCRIPTION, props.user_language)}
                        input_key="description"
                        prefill={prefill("description")}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.TEXT}
                        multiline={true}
                        style={{ minHeight: 300 }}
                    />
                </Col>
            </Row>
        </RBSModal>
    );
};
